import type { Room, RoomFilter } from "@catmail/shared";
import { MessageSquarePlus, Plus, Search, SearchX } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Virtuoso } from "react-virtuoso";

import { Button, EmptyState, IconButton, Skeleton, TextField } from "@/components";
import { cn } from "@/lib/cn";
import { ENTER } from "@/lib/motion";

import { RoomListItem } from "../RoomListItem";

const PANEL_TITLE: Record<RoomFilter, string> = {
  all: "전체",
  direct: "1:1 대화",
  group: "여러 명",
  quiet: "조용한 방",
};

/** 탭마다 비었을 때 할 말이 다르다. "전체"가 비었을 때만 처음 온 사람이다. */
const EMPTY_HINT: Record<RoomFilter, string> = {
  all: "누구에게든 첫 메일을 보내면 여기에 대화로 쌓여요.",
  direct: "한 사람과 주고받은 대화가 여기 모여요.",
  group: "여러 명이 함께 있는 대화가 여기 모여요.",
  quiet: "광고와 알림 메일이 여기로 와요. 아직 온 게 없네요.",
};

type RoomListPanelProps = {
  filter: RoomFilter;
  /**
   * 지금 쓰고 있는 주소. 제목 밑에 한 줄로 적는다.
   *
   * 갈아타는 자리는 프로필 팝오버 안이라 닫으면 사라진다. 그러면 목록에 방이
   * 적을 때 **갈아탄 것인지 메일이 없는 것인지** 구분이 안 간다. 여기 늘 적혀
   * 있으면 그 물음이 생기지 않는다.
   */
  address?: string | null;
  rooms: Room[];
  /** 아직 목록이 오지 않았다. 빈 것과 다르게 보여야 한다 */
  loading?: boolean;
  selectedRoomId: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (roomId: string) => void;
  onOpenProfile: (roomId: string) => void;
  onNewChat: () => void;
  /** 목록 끝에 닿으면 더 불러온다. */
  onLoadMore?: () => void;
  /** 목록 아래 붙는 것 — 되돌리기 막대나 "모두 읽음" 줄 */
  footer?: ReactNode;
  /** 사용자가 끌어서 정한 폭(px). 기본값은 usePanelWidths가 들고 있다 */
  width: number;
  /**
   * 좁은 화면에서 다크 레일을 대신하는 줄 (MobileNav). **목록 맨 아래**에 붙는다.
   *
   * 위에 두면 제목·검색과 겹쳐 머리가 세 겹이 되고, 한 손으로 들었을 때 제일
   * 자주 누르는 것(필터·내 프로필)이 제일 먼 곳에 놓인다. 디스코드도 사용자
   * 영역을 왼쪽 아래에 둔다.
   */
  nav?: ReactNode;
  /** 배치만 — 좁은 화면에서 방을 열면 이 칸을 숨긴다 */
  className?: string;
};

/** 시안 가운데 흰 패널 */
export function RoomListPanel({
  filter,
  address,
  rooms,
  loading = false,
  selectedRoomId,
  query,
  onQueryChange,
  onSelect,
  onOpenProfile,
  onNewChat,
  onLoadMore,
  footer,
  width,
  nav,
  className,
}: RoomListPanelProps) {
  return (
    // 오른쪽 경계선은 옆에 붙는 ResizeHandle이 그린다. 여기서도 그으면 두 줄이 된다.
    //
    // 폭은 CSS 변수를 거쳐 넘긴다. 인라인 style에는 미디어 쿼리를 걸 수 없어서,
    // 좁은 화면에서는 화면을 꽉 채우고 넓은 화면에서만 끌어 정한 폭을 쓰려면
    // 값을 변수로 넘기고 클래스 쪽에서 갈라야 한다.
    <div
      style={{ "--list-width": `${width}px` } as CSSProperties}
      className={cn(
        "flex w-full min-w-0 flex-col bg-surface md:w-[var(--list-width)] md:shrink-0",
        className,
      )}
    >
      <div className="flex flex-col gap-4 px-5.5 pt-5 pb-4 md:pt-6.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <h1 className="text-[22px] font-bold tracking-tight text-ink">
              {PANEL_TITLE[filter]}
            </h1>
            {/*
              지금 쓰는 주소.

              예전에는 주소가 둘 이상일 때만 적었다 — 고를 것이 없는데 적어두면
              자리만 차지한다고 봤다. 지금은 늘 적는다. 좁은 화면에는 레일이
              없어서 이 줄이 사라지면 **내가 어느 주소로 보고 있는지 화면
              어디에도 안 나온다.** 주소는 걸러 보는 조건이 아니라 지금 무엇이
              오고 무엇으로 나가는지를 정하는 값이다(architecture.md §1).
            */}
            {address ? (
              <p className="text-muted-3 truncate text-[12px]">{address}</p>
            ) : null}
          </div>
          <IconButton
            variant="filled"
            label="새 채팅"
            onClick={onNewChat}
            className="size-8.5"
          >
            <Plus />
          </IconButton>
        </div>

        <TextField
          variant="filled"
          size="sm"
          label="이름으로 찾기"
          placeholder="이름으로 찾기"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          leading={<Search />}
        />
      </div>

      {/*
        비어 있음을 말하기 전에 **다 왔는지부터** 확인한다. 예전에는 목록이
        도착하기 전 잠깐 "아직 대화가 없어요"가 떠서, 처음 온 사람이 빈 앱을
        받았다고 오해했다.
      */}
      {loading ? (
        <RoomListLoading />
      ) : rooms.length === 0 ? (
        query ? (
          <EmptyState
            icon={<SearchX />}
            title="찾는 대화가 없어요"
            description={`"${query}"와(과) 맞는 이름·주소·본문이 없어요.`}
          />
        ) : (
          <EmptyState
            icon={<MessageSquarePlus />}
            title="아직 대화가 없어요"
            description={EMPTY_HINT[filter]}
            actions={
              // 조용한 방은 내가 만드는 곳이 아니라 들어오는 곳이다.
              filter === "quiet" ? null : (
                <Button size="sm" onClick={onNewChat}>
                  새 채팅
                </Button>
              )
            }
          />
        )
      ) : (
        <Virtuoso
          // 탭을 옮기면 다른 목록이므로 처음부터 다시 그린다. 스크롤 위치가
          // 앞 탭에 남아 있으면 새 목록을 중간부터 보게 된다.
          key={filter}
          className={`flex-1 ${ENTER}`}
          data={rooms}
          // 끝에서 두 화면쯤 남았을 때 미리 부른다. 바닥에 닿고 나서 부르면
          // 스크롤이 한 번 멈췄다 이어진다.
          increaseViewportBy={{ top: 0, bottom: 600 }}
          endReached={onLoadMore}
          computeItemKey={(_, room) => room.id}
          itemContent={(_, room) => (
            <RoomListItem
              room={room}
              selected={room.id === selectedRoomId}
              onSelect={onSelect}
              onOpenProfile={onOpenProfile}
            />
          )}
        />
      )}

      {footer ? <div className="shrink-0">{footer}</div> : null}

      {nav}
    </div>
  );
}

/** 도착할 방들의 자리. 행 생김새는 RoomListItem을 따라간다. */
function RoomListLoading() {
  return (
    <div className="flex-1">
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <div key={row} className="flex items-center gap-3.5 px-5.5 py-3.75">
          <Skeleton className="size-12 shrink-0 rounded-avatar" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-32" />
            {/* 미리보기 줄은 길이가 제각각이라, 다 같으면 표처럼 보인다 */}
            <Skeleton className={row % 2 === 0 ? "h-3 w-full" : "h-3 w-3/5"} />
          </div>
        </div>
      ))}
    </div>
  );
}
